import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef, type SubmitEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';
import { useFinpspWithdrawal } from '../../../mutations/finpsp';
import { createFinpspWithdrawalSchema } from '../../../schemas/finpsp';
import { FINPSP_ENABLED_BANK_TYPES, FINPSP_WITHDRAWAL_TYPE } from '../../../constants/finpsp';
import useLocalization from '../../useLocalization';
import { useFinpspWithdrawalTypes } from './useFinpspWithdrawalTypes';

export const useFinpspWithdrawalForm = (paymentProvider: string | null) => {
  const { t } = useLocalization();
  const mutation = useFinpspWithdrawal();
  const withdrawalTypes = useFinpspWithdrawalTypes(paymentProvider);

  const submitting = useRef(false);
  const schema = useMemo(() => createFinpspWithdrawalSchema(t), [t]);
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { amount: '', type: withdrawalTypes[0] ?? FINPSP_WITHDRAWAL_TYPE.P2A, customer_phone: '' },
  });

  const selectedType = useWatch({ control: form.control, name: 'type' });
  const withdrawalType = withdrawalTypes.includes(selectedType)
    ? selectedType
    : (withdrawalTypes[0] ?? FINPSP_WITHDRAWAL_TYPE.P2A);
  const bankType = FINPSP_ENABLED_BANK_TYPES.find((method) => method === paymentProvider);

  const submit = (input: z.output<typeof schema>) => {
    if (submitting.current || !bankType || !withdrawalTypes.includes(input.type)) {
      return;
    }

    submitting.current = true;

    mutation.mutate(
      {
        ...input,
        bank_type: bankType,
      },
      {
        onSettled: () => {
          submitting.current = false;
        },
      }
    );
  };

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    form.setValue('type', withdrawalType);
    void form.handleSubmit(submit)(event);
  };

  return {
    form,
    withdrawalType,
    withdrawalTypes,
    isSubmitting: mutation.isPending || form.formState.isSubmitting,
    onSubmit,
  };
};
